import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export default function TargetedPromoAdminList() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const fetchPromos = async (opts = {}) => {
    const nextPage   = opts.page   ?? page;
    const nextLimit  = opts.limit  ?? limit;
    const nextSearch = opts.search ?? search;
    const nextStatus = opts.status ?? status;

    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/admin/promo-codes", {
        params: {
          promo_code_type: "TARGETED",
          search: nextSearch || undefined,
          status: nextStatus !== "" ? nextStatus : undefined,
          page: nextPage,
          limit: nextLimit,
          sortBy: "created_at",
          order: "DESC",
        },
      });
      setRows(res.data?.data || []);
      setMeta(res.data?.meta || { total: 0, page: nextPage, limit: nextLimit, totalPages: 1 });
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to load targeted promo codes";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchPromos({ page: 1, search, status });
  }, [status]);

  const onApply = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPromos({ page: 1, search, status });
  };

  const openCreate = () => navigate("/admin/targeted-promo-codes/create");
  const openEdit   = (promo) => navigate(`/admin/targeted-promo-codes/edit/${promo.promo_id}`);

  const deactivate = async (promo_id) => {
    if (!window.confirm("Deactivate this targeted promo?")) return;
    try {
      await api.delete(`/admin/promo-codes/${promo_id}`);
      toast.success("Promo deactivated");
      fetchPromos({ search, status });
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Deactivate failed");
    }
  };

  const deletePromo = async (promo_id) => {
    if (!window.confirm("Permanently delete this targeted promo? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/promo-codes/${promo_id}/hard`);
      toast.success("Promo deleted");
      fetchPromos({ search, status });
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Delete failed");
    }
  };

  const goPrev = () => {
    if (meta.page <= 1) return;
    const p = meta.page - 1;
    setPage(p);
    fetchPromos({ page: p, search, status });
  };

  const goNext = () => {
    if (meta.page >= meta.totalPages) return;
    const p = meta.page + 1;
    setPage(p);
    fetchPromos({ page: p, search, status });
  };

  const onLimitChange = (e) => {
    const v = Number(e.target.value) || 10;
    setLimit(v);
    setPage(1);
    fetchPromos({ page: 1, limit: v, search, status });
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Targeted Promo Codes</h1>
          <p className="text-sm text-gray-500 mt-1">
            Personalized offers with instant discount + NavCash cashback
          </p>
        </div>
        <button
          className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90"
          onClick={openCreate}
        >
          + Create Targeted Promo
        </button>
      </div>

      <form onSubmit={onApply} className="mt-6 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-3 border rounded-2xl p-4">
        <div className="w-full sm:w-auto sm:min-w-[240px]">
          <label className="text-sm font-semibold">Search</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Search by code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-auto sm:min-w-[160px]">
          <label className="text-sm font-semibold">Status</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>

        <div className="w-full sm:w-auto sm:min-w-[140px]">
          <label className="text-sm font-semibold">Per page</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={limit}
            onChange={onLimitChange}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>

        <button
          type="submit"
          className="w-full sm:w-auto rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90"
        >
          Apply
        </button>
      </form>

      {err ? <div className="mt-4 text-sm text-red-600">{err}</div> : null}

      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span>Targeted Promo List</span>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              onClick={goPrev}
              disabled={meta.page <= 1 || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
              type="button"
            >
              Prev
            </button>
            <span className="text-gray-700">
              Page <b>{meta.page}</b> / <b>{meta.totalPages}</b> (Total: <b>{meta.total}</b>)
            </span>
            <button
              onClick={goNext}
              disabled={meta.page >= meta.totalPages || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
              type="button"
            >
              Next
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No targeted promo codes found.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-t bg-white">
              <tr className="text-left">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Discount</th>
                <th className="px-4 py-3">Cashback</th>
                <th className="px-4 py-3">Trip</th>
                <th className="px-4 py-3">Max/User</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="border-t">
              {rows.map((p) => (
                <tr key={p.promo_id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">{p.code}</td>

                  <td className="px-4 py-3">
                    {p.discount_type === "PERCENT"
                      ? `${p.discount_value}%`
                      : `₹${p.discount_value}`}
                    {p.discount_type === "PERCENT" && p.max_booking_discount_amount
                      ? ` (max ₹${p.max_booking_discount_amount})`
                      : ""}
                  </td>

                  <td className="px-4 py-3">
                    {p.cashback_value
                      ? p.cashback_type === "PERCENT"
                        ? `${p.cashback_value}% NavCash`
                        : `₹${p.cashback_value} NavCash`
                      : "—"}
                  </td>

                  <td className="px-4 py-3">{p.trip_type}</td>
                  <td className="px-4 py-3">{p.max_usage_per_user}</td>

                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                        Number(p.status) === 1
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {Number(p.status) === 1 ? "Active" : "Inactive"}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                        onClick={() => openEdit(p)}
                      >
                        Edit
                      </button>
                      {Number(p.status) === 1 ? (
                        <button
                          type="button"
                          className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                          onClick={() => deactivate(p.promo_id)}
                        >
                          Deactivate
                        </button>
                      ) : null}

                      <button
                        type="button"
                        className="rounded-lg border border-red-200 text-red-600 px-3 py-1 text-xs font-semibold hover:bg-red-50"
                        onClick={() => deletePromo(p.promo_id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
